import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import reactUseWebSocket, {ReadyState} from 'react-use-websocket';

import { type WebSocketMessage } from '../models/index.js';

const useWebSocket = <T = unknown>() => {
  const { getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState('');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage<T>>();
  const [messages, setMessages] = useState<WebSocketMessage<T>[]>([]);
  const [websocket, setWebsocket] = useState<WebSocket>();
  useEffect(() => {
    const getToken = async () => {
      const _accessToken = await getAccessTokenSilently();
      setAccessToken(_accessToken);
    };
    getToken();
  }, [getAccessTokenSilently, setAccessToken]);


  const { getWebSocket, sendJsonMessage, lastJsonMessage, readyState } = reactUseWebSocket<WebSocketMessage<T>>(
    // Use a promise to get the websocket URL in order to make sure the access token is available
    import.meta.env.VITE_WS_BASE_URL,
    {
      share: true,
      ...(accessToken && { protocols: accessToken }),
      retryOnError: true,
      reconnectAttempts: 3,
      reconnectInterval: 3000,
      shouldReconnect: () => true,
      heartbeat: {
        message: JSON.stringify({event: 'ping'}),
        returnMessage:  JSON.stringify({event: 'pong'}),
        timeout: 60000, // 1 minute, if no response is received, the connection will be closed
        interval: 25000, // every 25 seconds, a ping message will be sent
      },
      // Some callbacks are available:
      // onOpen: (event) => {},
      // onClose(event) {},
      // onError(event) {},
    },
    !!accessToken,
  );

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      const _websocket = getWebSocket() as WebSocket;
      setWebsocket(_websocket);
    }
  }, [readyState, getWebSocket, setWebsocket]);

  useEffect(() => {
    if (lastJsonMessage !== null) {
      setLastMessage(lastJsonMessage);
      setMessages((previousMessages) => previousMessages.concat(lastJsonMessage));
    }
  }, [lastJsonMessage, setLastMessage, setMessages]);
  return {
    websocket,
    messages,
    lastMessage,
    sendJsonMessage,
  };
};


export default useWebSocket;
